import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/components/system";
import { parseImport, type ImportRecord } from "@/lib/vocab/import";
import { formatError } from "@/lib/format-error";

/**
 * /learning/vocabulary/import (BW18 PR-J).
 *
 * Accepts CSV / JSON pasted text or a file. Live-previews the parsed
 * records, then bulk-inserts on submit. New cards start with default
 * FSRS state (state='new', due=now), so they enter the next review queue.
 */

const SAMPLE_CSV = `source_text,source_lang,tags,example_sentence,phonetic_ipa,notes,en,de,it,es
chien,fr,animals,Le chien aboie.,ʃjɛ̃,,dog,Hund,cane,perro
bonjour,fr,greetings|formal,Bonjour tout le monde !,bɔ̃ʒuʁ,Greeting,hello,Hallo,ciao,hola`;

const ALL_TARGET_LANGS = ["en", "de", "fr", "it", "es"] as const;

export function LearningVocabularyImport() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const [text, setText] = useState("");
  const [defaultSource, setDefaultSource] = useState("fr");
  const [targetLangs, setTargetLangs] = useState<Set<string>>(
    () => new Set(["en", "de"]),
  );

  // The default-target lang is the first selected chip; falls back to en
  // when nothing is selected so legacy single-target rows still parse.
  const primaryTarget = Array.from(targetLangs)[0] ?? "en";
  const parsed = text.trim() ? parseImport(text, defaultSource, primaryTarget) : null;

  const importMut = useMutation({
    mutationFn: async (records: ImportRecord[]) => {
      if (!userId) throw new Error("Not signed in");
      const filtered = records
        .map((r) => ({
          ...r,
          translations:
            targetLangs.size === 0
              ? r.translations
              : r.translations.filter((t) => targetLangs.has(t.target_lang)),
        }))
        .filter((r) => r.translations.length > 0);
      if (filtered.length === 0) throw new Error("No translations match the selected languages");

      const cardRows = filtered.map((r) => ({
        user_id: userId,
        source_text: r.source_text,
        source_lang: r.source_lang,
        example_sentence: r.example_sentence ?? null,
        phonetic_ipa: r.phonetic_ipa ?? null,
        notes: r.notes ?? null,
        tags: r.tags,
      }));
      const { data: inserted, error: cardErr } = await supabase
        .from("vocab_card")
        .insert(cardRows)
        .select("id");
      if (cardErr) throw cardErr;
      const ids = (inserted ?? []).map((row) => row.id);
      if (ids.length !== filtered.length) {
        throw new Error("Card insert returned an unexpected row count");
      }
      const translationRows = filtered.flatMap((rec, idx) =>
        rec.translations.map((t) => ({
          user_id: userId,
          card_id: ids[idx],
          target_lang: t.target_lang,
          target_text: t.target_text,
          example_sentence: t.example_sentence ?? null,
          phonetic_ipa: t.phonetic_ipa ?? null,
          notes: t.notes ?? null,
        })),
      );
      const { error: txErr } = await supabase
        .from("vocab_translation")
        .insert(translationRows);
      if (txErr) throw txErr;
      return { cards: ids.length, translations: translationRows.length };
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ["vocab_due_count"] });
      void qc.invalidateQueries({ queryKey: ["vocab_queue"] });
      toast.show(
        `${result.cards} cards (${result.translations} translations) imported.`,
      );
      navigate("/learning/vocabulary");
    },
    onError: (e) => toast.show(`Import failed, ${formatError(e)}`),
  });

  function toggleLang(lang: string) {
    setTargetLangs((prev) => {
      const next = new Set(prev);
      if (next.has(lang)) next.delete(lang);
      else next.add(lang);
      return next;
    });
  }

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      setText(typeof reader.result === "string" ? reader.result : "");
    };
    reader.readAsText(file);
  }

  return (
    <article aria-labelledby="vocab-import-heading">
      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
        }}
      >
        <Link to="/learning/vocabulary" style={{ color: "var(--fg-3)" }}>
          Vocabulary
        </Link>{" "}
        · Import
      </p>
      <h1
        id="vocab-import-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-6)",
        }}
      >
        Drop in cards.
      </h1>

      <section
        style={{
          background: "var(--bg-2)",
          border: "1px solid var(--hair)",
          borderRadius: "var(--radius-card)",
          padding: "var(--space-5)",
          display: "grid",
          gap: "var(--space-3)",
        }}
      >
        <label style={{ display: "grid", gap: 4 }}>
          <span style={labelStyle()}>Source language</span>
          <input
            value={defaultSource}
            onChange={(e) => setDefaultSource(e.target.value)}
            style={inputStyle()}
          />
        </label>
        <div style={{ display: "grid", gap: 4 }}>
          <span style={labelStyle()}>Target languages</span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {ALL_TARGET_LANGS.map((lang) => {
              const active = targetLangs.has(lang);
              return (
                <button
                  key={lang}
                  type="button"
                  onClick={() => toggleLang(lang)}
                  aria-pressed={active}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    fontSize: "var(--type-body-s)",
                    cursor: "pointer",
                    background: active ? "var(--accent-wash)" : "var(--bg-3)",
                    color: active ? "var(--fg)" : "var(--fg-2)",
                    border: `1px solid ${active ? "var(--accent-dim)" : "var(--hair-2)"}`,
                    fontWeight: active ? 500 : 400,
                  }}
                >
                  {lang.toUpperCase()}
                </button>
              );
            })}
          </div>
          <span style={{ ...labelStyle(), textTransform: "none", color: "var(--fg-3)" }}>
            Tap to include or exclude. Leave empty to import every language column found.
          </span>
        </div>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={labelStyle()}>CSV or JSON</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={SAMPLE_CSV}
            spellCheck={false}
            style={{
              ...inputStyle(),
              height: 220,
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-body-s)",
              padding: "var(--space-3)",
              resize: "vertical",
            }}
          />
        </label>
        <input
          type="file"
          accept=".csv,.tsv,.json,.txt"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
          style={{ fontSize: "var(--type-body-s)", color: "var(--fg-3)" }}
        />

        {parsed && (
          <PreviewSummary parsed={parsed} targetLangs={targetLangs} />
        )}

        <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
          <Link
            to="/learning/vocabulary"
            style={{
              ...primaryButtonStyle(),
              background: "transparent",
              color: "var(--fg)",
              border: "1px solid var(--hair-2)",
            }}
          >
            Cancel
          </Link>
          <button
            type="button"
            onClick={() => parsed && importMut.mutate(parsed.records)}
            disabled={!parsed || parsed.records.length === 0 || importMut.isPending}
            style={{
              ...primaryButtonStyle(),
              cursor:
                !parsed || parsed.records.length === 0 || importMut.isPending
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            <Upload size={14} strokeWidth={1.6} style={{ marginRight: 6 }} />
            {importMut.isPending
              ? "Importing…"
              : `Import ${parsed?.records.length ?? 0} cards`}
          </button>
        </div>
      </section>
    </article>
  );
}

function PreviewSummary({
  parsed,
  targetLangs,
}: {
  parsed: { records: ImportRecord[]; errors: string[] };
  targetLangs: Set<string>;
}) {
  const sample = parsed.records.slice(0, 3);
  const visibleTranslations = (record: ImportRecord) =>
    targetLangs.size === 0
      ? record.translations
      : record.translations.filter((t) => targetLangs.has(t.target_lang));
  const totalTranslations = parsed.records.reduce(
    (acc, r) => acc + visibleTranslations(r).length,
    0,
  );
  return (
    <div
      style={{
        padding: "var(--space-3)",
        background: "var(--bg-3)",
        borderRadius: "var(--radius-card)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
        display: "grid",
        gap: "var(--space-2)",
      }}
    >
      <p style={{ margin: 0 }}>
        <strong>{parsed.records.length}</strong> cards · {totalTranslations} translations ready to import
        {parsed.errors.length > 0 && (
          <span style={{ color: "var(--brick)", marginLeft: 8 }}>
            · {parsed.errors.length} skipped
          </span>
        )}
      </p>
      {sample.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gap: 4,
            color: "var(--fg-3)",
          }}
        >
          {sample.map((r, i) => {
            const translations = visibleTranslations(r);
            const summary = translations
              .map((t) => `${t.target_lang.toUpperCase()}: ${t.target_text}`)
              .join("  ·  ");
            return (
              <li key={i} className="mono">
                {r.source_text} ({r.source_lang}) → {summary || "(no matching languages)"}
              </li>
            );
          })}
        </ul>
      )}
      {parsed.errors.slice(0, 3).map((err, i) => (
        <p
          key={i}
          style={{ margin: 0, color: "var(--brick)", fontSize: "var(--type-meta)" }}
        >
          {err}
        </p>
      ))}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    height: 36,
    padding: "0 var(--space-3)",
    background: "var(--bg-3)",
    color: "var(--fg)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-card)",
    fontFamily: "var(--font-sans)",
    fontSize: "var(--type-body)",
  };
}

function labelStyle(): React.CSSProperties {
  return {
    fontSize: "var(--type-meta)",
    color: "var(--fg-3)",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  };
}

function primaryButtonStyle(): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 38,
    padding: "0 var(--space-4)",
    background: "var(--accent)",
    color: "#0a0b0d",
    border: "none",
    borderRadius: "var(--radius-card)",
    fontSize: "var(--type-body-s)",
    fontWeight: 500,
    textDecoration: "none",
  };
}
